import type { ICustomerFormData } from '@booking-portal/modules/booking-flow/types/booking-flow-types';
import type {
  ICreateInterestRegistrationRequest,
  ICustomerPayload,
  IInterestRegistrationConsentTypes,
  IRequestInterestFormData,
} from '@booking-portal/types';
import type { Maybe } from 'shared/types';

function requiredOrThrow<T>(data: T | undefined | null, errorHint = ''): T {
  if (data == null) {
    throw new Error(
      errorHint
        ? 'value null/undefined: ' + errorHint
        : 'given data is null or undefined'
    );
  }

  return data as T;
}

function getCustomerPayload(
  form: Partial<ICustomerFormData>
): ICustomerPayload {
  const r = requiredOrThrow;

  return {
    customerType: r(form.customerType, 'customerType'),
    street: r(form.street, 'street'),
    city: r(form.city, 'city'),
    postalCode: r(form.zip, 'zip'),
    companyName:
      form.customerType === 'business'
        ? r(form.companyName, 'companyName')
        : form.companyName,
    companyTaxIdentificationNumber: form.vatNumber || null,
    companyVatType: form.companyVatType || null,
    firstname: r(form.firstName, 'firstName'),
    lastname: r(form.lastName, 'lastName'),
    countryId: r(form.countryId, 'country.id'),
    phoneNumber: r(form.phone?.number, 'phone'),
    phonePrefix: r(form.phone?.prefix, 'phone'),
    email: r(form.email, 'email'),
  };
}

export function getCustomerFormDataAndValidate(
  givenCustomer: Partial<ICustomerFormData>
) {
  const customer = getCustomerPayload(givenCustomer);
  const r = requiredOrThrow;

  const isBusiness = customer.customerType === 'business';

  return {
    countryId: r(customer.countryId, 'countryId'),
    lastname: r(customer.lastname, 'lastname'),
    firstname: r(customer.firstname, 'firstname'),
    companyTaxIdentificationNumber: isBusiness
      ? customer.companyTaxIdentificationNumber || null
      : undefined,
    companyVatType: isBusiness ? customer.companyVatType || null : undefined,
    companyName: isBusiness ? customer.companyName || null : undefined,
    postalCode: r(customer.postalCode, 'postalCode'),
    city: r(customer.city, 'city'),
    street: r(customer.street, 'street'),
    customerType: r(customer.customerType, 'customerType'),
    email: r(customer.email, 'email'),
    phoneNumber: r(customer.phoneNumber, 'phone'),
    phonePrefix: r(customer.phonePrefix, 'phone'),
  };
}

export function getFullName(item?: {
  firstname?: Maybe<string>;
  lastname?: Maybe<string>;
}) {
  const fullName = [item?.firstname, item?.lastname]
    .map((name) => name?.trim())
    .filter((name) => name)
    .join(' ');

  return fullName ? fullName : '';
}

/**
 * Converts the register-interest form consents (record) to the server's
 * array shape.
 */
export function fromInterestRequestFormConsentsToPayload(
  input: IRequestInterestFormData['consents']
): ICreateInterestRegistrationRequest['consents'] {
  const keys = Object.keys(input || {}) as IInterestRegistrationConsentTypes[];
  return keys.map((type) => ({
    type,
    accepted: !!input[type],
  }));
}
